import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Combobox, ComboboxOption, ComboboxOptions, Transition } from '@headlessui/react'
import { Fragment, ReactElement, useMemo, useRef, useState, UIEvent } from 'react'
import Input from './input'

export interface Option {
    id: number | string
    label: string
    disabled?: boolean
    meta?: any
}

interface DropdownProps {
    error?: { key: string; values: object } | string
    options: Option[]
    value: Option['id'] | undefined
    onChange: (value: Option['id']) => void
    onSearchTermChange?: (value: string) => void
    onOptionNotFound?: (searchTerm: string) => void
    label?: string
    required?: boolean
    placeholder?: string
    disabled?: boolean
    unit?: string
    className?: string
    ulClassName?: string
    searchable?: boolean
    renderOption?: (option: Option) => ReactElement
    renderSelectedOption?: (option: Option) => ReactElement
    hideLabel?: boolean
    onEndReached?: () => void
    info?: string
    showDownIcon?: boolean
    dataCy?: string
}

const Dropdown: React.FC<DropdownProps> = ({
    options,
    value,
    onChange,
    label,
    required = false,
    placeholder,
    disabled,
    error,
    unit,
    className,
    ulClassName,
    hideLabel,
    searchable = true,
    renderOption,
    onSearchTermChange,
    onEndReached,
    onOptionNotFound,
    info,
    renderSelectedOption,
    showDownIcon = true,
    dataCy,
}) => {
    const ref = useRef<HTMLSelectElement | null>(null)
    const listboxButtonStyle = useMemo(() => {
        return `ringo-dropdown text-left relative ${className}`
    }, [disabled])

    const [searchTerm, setSearchTerm] = useState('')
    const selectableOptions = useMemo(() => {
        if (!searchTerm || onSearchTermChange) {
            return options
        }
        return options.filter((x) => x.label && x.label?.toLowerCase().includes(searchTerm.toLowerCase()))
    }, [options, searchTerm])

    const getDisplayValue = (val: any) => {
        if (val == null) return ''
        const selectedOption = options.find((option) => option.id === val)
        return selectedOption ? selectedOption.label : ''
    }

    const handleScroll = (event: UIEvent<HTMLDivElement, UIEvent>) => {
        const target = event.currentTarget
        if (target.scrollHeight - target.scrollTop === target.clientHeight) {
            onEndReached && onEndReached()
        }
    }

    const handleOnBlur = () => {
        if (!searchTerm || !onOptionNotFound) {
            return
        }
        const isOptionFound = selectableOptions.some((option) => option.label.toLowerCase() === searchTerm.toLowerCase())
        if (!isOptionFound) {
            onOptionNotFound(searchTerm)
        }
    }

    return (
        <div className="relative">
            <Input label={label} required={required} error={error} hideLabel={hideLabel} info={info}>
                <div className="relative">
                    <select style={{ display: 'none' }} ref={ref} />

                    <Combobox
                        value={value}
                        disabled={disabled}
                        onChange={(value) => {
                            onChange(value as any)
                            setSearchTerm('')
                            if (ref.current) {
                                ref.current.dispatchEvent(new Event('change', { bubbles: true }))
                            }
                        }}
                    >
                        <Combobox.Button className={listboxButtonStyle} as="div" data-cy={dataCy}>
                            <div className="block overflow-visible max-tablet:min-h-[20px] min-h-[24px]">
                                {searchable ? (
                                    <Combobox.Input
                                        displayValue={(item) => getDisplayValue(item)}
                                        onChange={(event) => {
                                            setSearchTerm(event.target.value)
                                            onSearchTermChange && onSearchTermChange(event.target.value)
                                        }}
                                        onBlur={handleOnBlur}
                                        className="w-full outline-hidden"
                                    />
                                ) : renderSelectedOption ? (
                                    renderSelectedOption(options.find((option) => option.id === value)!)
                                ) : (
                                    <>{getDisplayValue(value) ?? placeholder}</>
                                )}
                            </div>
                            {showDownIcon && (
                                <span className="text-xs pointer-events-none absolute top-1/2 right-0 transform -translate-x-1/2 -translate-y-1/2 flex items-center">
                                    <FontAwesomeIcon icon={['fas', 'chevron-down']} />
                                </span>
                            )}
                        </Combobox.Button>
                        <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setSearchTerm('')}
                        >
                            <ComboboxOptions
                                className={`z-50 border-b border-gray-200 absolute bg-white w-full max-h-72 overflow-y-auto ${ulClassName}`}
                                onScroll={handleScroll as any}
                            >
                                {selectableOptions.map((item) => (
                                    <ComboboxOption
                                        key={item.id}
                                        value={item.id}
                                        data-cy={dataCy ? `${dataCy}-option-${item.id}` : undefined}
                                        className="text-base text-left placeholder-italic w-full border-l border-r border-[#d4d4d4] focus:outline-hidden max-tablet:text-sm"
                                    >
                                        {({ selected, active }) => (
                                            <div className={`py-2 px-2 cursor-pointer ${active ? 'bg-[#f7f7f7]' : null} ${selected ? 'bg-[#ededed]' : null}`}>
                                                {renderOption ? renderOption(item) : item.label}
                                            </div>
                                        )}
                                    </ComboboxOption>
                                ))}
                            </ComboboxOptions>
                        </Transition>
                    </Combobox>
                    {unit && (
                        <p className="absolute leading-none! text-gray-500 max-tablet:text-sm top-1/2 right-4 transform -translate-x-1/2 -translate-y-1/2">
                            {unit}
                        </p>
                    )}
                </div>
            </Input>
        </div>
    )
}

export default Dropdown
